import { lazy, Suspense, useEffect, useState } from 'react';
import { bindActionCreators } from '@reduxjs/toolkit';
import { connect, useSelector } from 'react-redux';

import Button from 'carbon-react/lib/components/button';
import Preview from 'carbon-react/lib/components/preview';
import { Tabs, Tab } from 'carbon-react/lib/components/tabs';

import CreateTimesheet from './CreateTimesheet';
import ErrorBoundary from '../../components/ErrorBoundary';
import Translate from '../../components/Translate/Translate';

import * as AutofillActions from '../../redux/Autofill/actions';
import * as TimesheetActions from '../../redux/Timesheet/actions';
import * as TimesheetsActions from '../../redux/Timesheets/actions';
import * as TimesheetEntriesActions from '../../redux/TimesheetEntries/actions';
import * as TimesheetRowsActions from '../../redux/TimesheetRows/actions';

import { loadingChunkRetry } from '../../helpers/utils';
import { useLocationQuery } from '../../helpers/hooks';
import { selectUserData } from '../../redux/selectors';
import toastr from '../../helpers/toastr';
import I18njs from 'i18n-js';

const TimesheetsTable = lazy(() => loadingChunkRetry(() => import('./TimesheetsTable')));

const Timesheets = ({ getTimesheets, resetAutofill, resetTimesheet, resetTimesheetEntries, resetTimesheetRows }) => {
  const query = useLocationQuery();
  const openCreateTimesheetModal = query.get('create_new_timesheet') === 'true';

  const [modalOpen, setModalOpen] = useState(false);

  const userData = useSelector(selectUserData);

  useEffect(() => {
    getTimesheets();
    resetAutofill();
    resetTimesheet();
    resetTimesheetEntries();
    resetTimesheetRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleCreateTimesheetModal = (open) => {
    if (userData.canCreateTimesheet === false) {
      toastr.warning(I18njs.t('timesheets.MustHaveEmployee'));
    } else {
      setModalOpen(open);
    }
  };

  useEffect(() => {
    if (openCreateTimesheetModal) {
      setModalOpen(true);
    }
  }, [openCreateTimesheetModal]);

  return (
    <div className="timesheets">
      <div className="header">
        <div className="title">
          <Translate scope="timesheets.Timesheets" data-cy="timesheets-title" />
        </div>
        <Button as="primary" onClick={() => toggleCreateTimesheetModal(true)} data-cy="timesheets-create-new-timesheet">
          <Translate scope="timesheets.CreateaNewTimesheet" />
        </Button>
      </div>
      <ErrorBoundary>
        <Suspense fallback={<Preview height="600px" loading />}>
          <Tabs align="left" position="top" selectedTabId="active-timesheets" setLocation={false}>
            <Tab
              errorMessage={''}
              infoMessage={''}
              warningMessage={''}
              title="Active Timesheets"
              tabId="active-timesheets"
              key="active-timesheets"
            >
              <div className="table-wrapper">
                <TimesheetsTable active openCreateTimesheetModal={toggleCreateTimesheetModal} />
              </div>
            </Tab>
            <Tab errorMessage={''} infoMessage={''} warningMessage={''} title="All Timesheets" tabId="all-timesheets" key="all-timesheets">
              <div className="table-wrapper">
                <TimesheetsTable openCreateTimesheetModal={toggleCreateTimesheetModal} />
              </div>
            </Tab>
          </Tabs>
        </Suspense>
      </ErrorBoundary>
      {modalOpen && (
        <CreateTimesheet
          onCancel={() => toggleCreateTimesheetModal(false)}
          duplicate={typeof modalOpen === 'object'}
          timesheet={modalOpen}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({});

const mapDispatchToProps = (dispatch) => {
  const _autofill = bindActionCreators(AutofillActions, dispatch);
  const _timesheet = bindActionCreators(TimesheetActions, dispatch);
  const _timesheets = bindActionCreators(TimesheetsActions, dispatch);
  const _timesheetEntries = bindActionCreators(TimesheetEntriesActions, dispatch);
  const _timesheetRows = bindActionCreators(TimesheetRowsActions, dispatch);
  return {
    getTimesheets: _timesheets.getTimesheetsRequest,
    resetAutofill: _autofill.resetAutofill,
    resetTimesheet: _timesheet.resetTimesheetState,
    resetTimesheetEntries: _timesheetEntries.resetTimesheetEntriesState,
    resetTimesheetRows: _timesheetRows.resetTimesheetRowsState,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Timesheets);
